import { z } from 'zod';
import { WallClockSchema } from './attendance';
import { PersonRefSchema, SiteRefSchema } from './property';
import { QuoteConversionRateSchema, SalesQuoteRowSchema } from './sales';

/**
 * Customers (docs/specs/sales-customers.md §3.5, §4.4; D83, D84): the `customers` read model
 * from dt_spar's `Customer`, the optional account manager set in Aroo, the nightly signals
 * (lapsed, drifting, high returns) and the list / record screens. Money is £ to the penny,
 * dates YYYY-MM-DD, invoice times London wall clock.
 */

const IsoDate = z.iso.date();
const Instant = z.iso.datetime();
const Period = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'YYYY-MM');

export const CUSTOMER_PERMISSIONS = {
  accountsView: 'customers.accounts.view',
  accountsManage: 'customers.accounts.manage',
} as const;

export const CustomerSignalKindSchema = z.enum(['lapsed', 'drifting', 'high_returns']);
export type CustomerSignalKind = z.infer<typeof CustomerSignalKindSchema>;
export const CUSTOMER_SIGNAL_LABELS: Record<CustomerSignalKind, string> = {
  lapsed: 'Lapsed',
  drifting: 'Drifting',
  high_returns: 'High returns',
};

/** One signal row: at a selling branch, or across every branch (`site` null). */
export const CustomerSignalSchema = z.object({
  kind: CustomerSignalKindSchema,
  /** The selling branch the signal was computed at; null = the account across every branch. */
  site: SiteRefSchema.nullable(),
  /** lapsed: days since the last invoice; drifting: current spend; high_returns: the ratio %. */
  value: z.number(),
  /** lapsed: the prior spend; drifting: the expected spend; high_returns: the sales. */
  baseline: z.number(),
  since: IsoDate,
  lastInvoiceDate: IsoDate.nullable(),
});
export type CustomerSignal = z.infer<typeof CustomerSignalSchema>;

export const CustomerListFilterSchema = z.enum([
  'all',
  'home',
  'served',
  'today',
  'active30',
  'call',
  'lapsed',
  'drifting',
  'returns',
  'top',
  'mine',
]);
export type CustomerListFilter = z.infer<typeof CustomerListFilterSchema>;
export const CUSTOMER_FILTER_LABELS: Record<CustomerListFilter, string> = {
  all: 'All',
  home: 'Home here',
  served: 'Served here',
  today: 'Bought today',
  active30: 'Active (30 days)',
  call: 'To call',
  lapsed: 'Lapsed',
  drifting: 'Drifting',
  returns: 'High returns',
  top: 'Top customers',
  mine: 'Mine',
};

export const CustomerListSortSchema = z.enum(['net90', 'net30', 'name', 'last_invoice']);
export type CustomerListSort = z.infer<typeof CustomerListSortSchema>;

export const CustomersListQuerySchema = z.object({
  /** A branch in scope, or omitted for every branch in scope. */
  site: z.uuid().optional(),
  filter: CustomerListFilterSchema.optional(),
  q: z.string().trim().max(60).optional(),
  sort: CustomerListSortSchema.optional(),
});
export type CustomersListQuery = z.infer<typeof CustomersListQuerySchema>;

export const CustomerListRowSchema = z.object({
  acct: z.string(),
  /** Null when the mirror has no `Customer` row for an account that sold. */
  name: z.string().nullable(),
  postcode: z.string().nullable(),
  homeSite: SiteRefSchema.nullable(),
  /** The raw home code when it is not a current site (closed, legacy, BAD_DEBT). */
  homeCodeRaw: z.string().nullable(),
  manager: PersonRefSchema.nullable(),
  lastInvoiceAt: z.string().nullable(),
  /** The account's figures across every branch. */
  netSales30: z.number(),
  netSales90: z.number(),
  invoices90: z.number().int(),
  boughtToday: z.boolean(),
  signals: z.array(CustomerSignalSchema),
});
export type CustomerListRow = z.infer<typeof CustomerListRowSchema>;

export const CustomerScopeKindSchema = z.enum(['company', 'branches', 'personal']);

export const CustomersListResponseSchema = z.object({
  filter: CustomerListFilterSchema,
  sort: CustomerListSortSchema,
  site: SiteRefSchema.nullable(),
  /** What the caller may see: every account, their branches' accounts, or the ones they served. */
  scope: z.object({
    kind: CustomerScopeKindSchema,
    sites: z.array(SiteRefSchema),
  }),
  rows: z.array(CustomerListRowSchema),
  /** More rows matched than were returned (search or filter to narrow). */
  truncated: z.boolean(),
  limit: z.number().int(),
  today: IsoDate,
});
export type CustomersListResponse = z.infer<typeof CustomersListResponseSchema>;

const Figures = z.object({
  netSales: z.number(),
  sales: z.number(),
  credits: z.number(),
  invoices: z.number().int(),
  /** Over the days whose cost is known (null when none are). */
  marginPct: z.number().nullable(),
  /** Days in the window with cost data, out of the days with any figure. */
  daysWithCost: z.number().int(),
  days: z.number().int(),
});

export const CustomerRecordSchema = z.object({
  acct: z.string(),
  /** The record's id for the Discussion thread. */
  id: z.uuid(),
  name: z.string().nullable(),
  postcode: z.string().nullable(),
  homeSite: SiteRefSchema.nullable(),
  homeCodeRaw: z.string().nullable(),
  cdisc: z.string().nullable(),
  ptype: z.string().nullable(),
  /** Autopart's own `Rep` label, kept as text only (D83). */
  repRaw: z.string().nullable(),
  /** Internal (CDisc exclusion) or a branch's own account: left out of the sales figures. */
  excluded: z.boolean(),
  firstSeenAt: Instant,
  lastInvoiceAt: z.string().nullable(),
  manager: z
    .object({
      person: PersonRefSchema,
      assignedBy: PersonRefSchema.nullable(),
      assignedAt: Instant,
    }) // prettier-ignore
    .nullable(),
  managerHistory: z.array(
    z.object({
      person: PersonRefSchema,
      assignedBy: PersonRefSchema.nullable(),
      assignedAt: Instant,
      endedAt: Instant.nullable(),
      endedBy: PersonRefSchema.nullable(),
    }),
  ),
  canManage: z.boolean(),
  today: IsoDate,
  figures: z.object({ today: Figures, days30: Figures, days90: Figures, months12: Figures }),
  /** Net sales per selling branch over 12 months (an account may buy at several). */
  byBranch: z.array(
    z.object({
      site: SiteRefSchema.nullable(),
      branchCode: z.string(),
      netSales: z.number(),
      invoices: z.number().int(),
      share: z.number().nullable(),
      lastInvoiceDate: IsoDate.nullable(),
    }),
  ),
  monthly: z.array(
    z.object({
      period: Period,
      netSales: z.number(),
      invoices: z.number().int(),
      marginPct: z.number().nullable(),
    }),
  ),
  /** The last 90 days, one point per day with a figure. */
  daily: z.array(z.object({ date: IsoDate, netSales: z.number(), invoices: z.number().int() })),
  signals: z.array(CustomerSignalSchema),
  /** S-M3 (D85): open quotes and the last 90 days' quotes with their conversion. */
  quotes: z.object({
    open: z.array(SalesQuoteRowSchema),
    recent: z.array(SalesQuoteRowSchema),
    rate90: QuoteConversionRateSchema,
    lastQuoteAt: z.string().nullable(),
  }),
  /** S-M4 (D86): the credit panel; always "not available yet" until the feed exists. */
  credit: z.object({ available: z.literal(false) }),
});
export type CustomerRecord = z.infer<typeof CustomerRecordSchema>;

/** S-M3: the account's recent invoices and credit notes, read live from the mirror (bounded). */
export const CustomerDocumentSchema = z.object({
  prefix: z.string(),
  document: z.string(),
  at: WallClockSchema,
  goods: z.number(),
  initials: z.string().nullable(),
  branchCode: z.string(),
  site: SiteRefSchema.nullable(),
  /** The invoice cites these quotes (from `quote_links`). */
  quotes: z.array(z.string()),
});
export type CustomerDocument = z.infer<typeof CustomerDocumentSchema>;

export const CustomerDocumentsSchema = z.object({
  acct: z.string(),
  /** Window start (12 months back) and the cap applied. */
  since: z.iso.date(),
  limit: z.number().int(),
  rows: z.array(CustomerDocumentSchema),
  truncated: z.boolean(),
});
export type CustomerDocuments = z.infer<typeof CustomerDocumentsSchema>;

export const CustomerDocumentLinesSchema = z.object({
  prefix: z.string(),
  document: z.string(),
  lines: z.array(
    z.object({
      seqno: z.number().int(),
      part: z.string(),
      description: z.string().nullable(),
      qty: z.number(),
      unit: z.number(),
      discPct: z.number(),
      value: z.number(),
    }),
  ),
});
export type CustomerDocumentLines = z.infer<typeof CustomerDocumentLinesSchema>;

export const CustomerManagerWriteSchema = z.object({
  /** Null ends the current assignment without a replacement. */
  personId: z.uuid().nullable(),
});
export type CustomerManagerWrite = z.infer<typeof CustomerManagerWriteSchema>;

export const ApplySuggestedManagersSchema = z.object({
  /** Accounts to apply; omitted = every suggestion the caller may apply. */
  accts: z.array(z.string().trim().min(1).max(10)).max(2_000).optional(),
});
export type ApplySuggestedManagers = z.infer<typeof ApplySuggestedManagersSchema>;

export const CustomerOkSchema = z.object({ ok: z.literal(true), applied: z.number().int() });

/** The Settings panel for the signal rules (§4.7): thresholds as data with a plain preview. */
export const CustomerSignalRuleSchema = z.object({
  code: CustomerSignalKindSchema,
  params: z.record(z.string(), z.number()),
  active: z.boolean(),
  updatedBy: PersonRefSchema.nullable(),
  updatedAt: Instant.nullable(),
});
export type CustomerSignalRule = z.infer<typeof CustomerSignalRuleSchema>;
export const CustomerSignalRulesSchema = z.array(CustomerSignalRuleSchema);

export const CustomerSignalRuleWriteSchema = z.object({
  params: z.record(z.string(), z.number().min(0).max(100_000)),
  active: z.boolean().optional(),
});
export type CustomerSignalRuleWrite = z.infer<typeof CustomerSignalRuleWriteSchema>;
